/**
 * Welle 1 (Cockpit-Intelligenz) W1-B — client wrappers for the finding-resolution
 * callables (`resolveCheckFinding` / `listCheckResolutions`).
 *
 * Reference: architecture-cockpit-intelligence.md §Interfaces (Backend —
 * Resolution-Callables) + §GoBD-/Audit-Konzept. Thin httpsCallable bindings (same
 * pattern as {@link ./advisorCockpitApi} / {@link ./kontierungApi}) — ALL hard
 * checks (Loop-B-Beta + Advisor-Scope, fingerprint derivation, role derivation,
 * append-only write) live server-side (`functions/src/moat/
 * cockpitResolutionCallable.ts`).
 *
 * DB→FE mapping (architecture §DB-Mapping-Layer): `check_resolutions.createdAt`
 * is a Firestore `Timestamp` on the backend but crosses the callable wire as
 * either an already-server-converted millis number OR a JSON-serialized
 * Timestamp (`{ _seconds, _nanoseconds }` / `{ seconds, nanoseconds }`) —
 * {@link parseTimestampMillis} accepts all three shapes defensively.
 * `resolutionType`/`resolvedByRole` are runtime-narrowed against their known
 * enum values; a malformed `check_resolutions` record is DISCARDED (list path,
 * logged) or rejected (single-write path, thrown) — never silently coerced
 * (fail-closed, Golden Rule "Typ-Konvertierungen an DB/API-Grenzen explizit").
 *
 * @module api/cockpitResolutionApi
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import logger from '../utils/logger';

/** How a finding was last acted on (mirror of the server `resolutionType`). */
export type CheckResolutionType = 'resolved' | 'ignored' | 'reopened';

/** Who resolved a finding — server-derived, never client-trusted. */
export type CheckResolutionRole = 'owner' | 'advisor';

const RESOLUTION_TYPES: readonly CheckResolutionType[] = ['resolved', 'ignored', 'reopened'];
const RESOLUTION_ROLES: readonly CheckResolutionRole[] = ['owner', 'advisor'];

/**
 * One append-only `check_resolutions/{autoId}` audit event, DB→FE mapped
 * (`createdAt` → millis). Mirror of the server `CheckResolutionDoc`.
 */
export interface CheckResolution {
  docId: string;
  checkId: string;
  findingFingerprint: string;
  resolutionType: CheckResolutionType;
  note: string;
  resolvedByUid: string;
  resolvedByRole: CheckResolutionRole;
  engineVersion: string | null;
  /** `check_resolutions.createdAt` (Firestore Timestamp) → FE millis. */
  createdAt: number;
}

export interface ResolveCheckFindingInput {
  mandantUid: string;
  docId: string;
  checkId: string;
  resolutionType: CheckResolutionType;
  /** Freitext-Begründung, 0..1000 Zeichen. Optional — server defaults to ''. */
  note?: string;
}

export interface ResolveCheckFindingResult {
  resolution: CheckResolution;
}

export interface ListCheckResolutionsResult {
  /** Full audit trail for one document, ascending by `createdAt` (server order). */
  resolutions: CheckResolution[];
}

/**
 * Parses a raw Firestore-Timestamp-ish value into epoch millis, accepting the
 * three shapes that can realistically cross a Cloud Functions callable wire:
 * an already-converted number, a JSON-serialized Admin `Timestamp`
 * (`_seconds`/`_nanoseconds` from its `toJSON()`), or a plain
 * `{seconds,nanoseconds}` object. Anything else ⇒ `null` (fail-closed —
 * callers discard/reject rather than fabricate a date).
 *
 * @param raw - The raw `createdAt`/`resolvedAt`/`lastSeenAt` value.
 * @returns Epoch millis, or `null` if unparseable.
 */
export function parseTimestampMillis(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    const seconds = obj._seconds ?? obj.seconds;
    const nanoseconds = obj._nanoseconds ?? obj.nanoseconds ?? 0;
    if (typeof seconds === 'number' && Number.isFinite(seconds)) {
      const nanos = typeof nanoseconds === 'number' && Number.isFinite(nanoseconds) ? nanoseconds : 0;
      return seconds * 1000 + Math.floor(nanos / 1e6);
    }
  }
  if (typeof raw === 'string') {
    const parsed = Date.parse(raw);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return null;
}

/**
 * Runtime-narrows a raw `check_resolutions` document into a {@link
 * CheckResolution}. Returns `null` (never throws) when a required scalar is
 * missing/wrong-typed or an enum value is unknown — fail-closed, mirrors
 * `messagesApi.mapDocToMessage`'s discard-not-coerce convention. Exported so
 * `cockpitDocumentDetailApi.ts` can reuse it verbatim for `resolutionTrail`
 * entries (same `check_resolutions` doc shape) — no copy-pasted mapper.
 *
 * @param raw - The raw resolution object from the callable response.
 * @returns A typed {@link CheckResolution}, or `null` if malformed.
 */
export function mapRawResolution(raw: unknown): CheckResolution | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;

  const resolutionType = r.resolutionType as unknown;
  if (typeof resolutionType !== 'string' || !RESOLUTION_TYPES.includes(resolutionType as CheckResolutionType)) {
    return null;
  }
  const resolvedByRole = r.resolvedByRole as unknown;
  if (typeof resolvedByRole !== 'string' || !RESOLUTION_ROLES.includes(resolvedByRole as CheckResolutionRole)) {
    return null;
  }
  if (
    typeof r.docId !== 'string' ||
    typeof r.checkId !== 'string' ||
    typeof r.findingFingerprint !== 'string' ||
    typeof r.resolvedByUid !== 'string'
  ) {
    return null;
  }
  const createdAt = parseTimestampMillis(r.createdAt);
  if (createdAt === null) return null;

  const note = typeof r.note === 'string' ? r.note : '';
  const engineVersion = typeof r.engineVersion === 'string' ? r.engineVersion : null;

  return {
    docId: r.docId,
    checkId: r.checkId,
    findingFingerprint: r.findingFingerprint,
    resolutionType: resolutionType as CheckResolutionType,
    note,
    resolvedByUid: r.resolvedByUid,
    resolvedByRole: resolvedByRole as CheckResolutionRole,
    engineVersion,
    createdAt,
  };
}

/**
 * Records a resolution event for one finding — "als geklärt markieren" /
 * "ignorieren" / "wieder öffnen" (GoBD-audit, append-only). The server derives
 * `resolvedByUid`/`resolvedByRole` from the authenticated caller and computes
 * the `findingFingerprint` itself from `document_checks/{docId}` — this input
 * never carries a fingerprint.
 *
 * @param input - mandant scope, target doc/check, action, optional note.
 * @returns the newly created resolution event.
 * @throws Error (normalised) with `code==='permission-denied'` for a foreign
 *   mandant, `code==='invalid-argument'` for an unresolvable docId/checkId, or
 *   on a malformed server response.
 */
export async function resolveCheckFinding(
  input: ResolveCheckFindingInput,
): Promise<ResolveCheckFindingResult> {
  const callable = httpsCallable<ResolveCheckFindingInput, { resolution: unknown }>(
    functions,
    'resolveCheckFinding',
  );
  try {
    const res = await callable(input);
    const mapped = mapRawResolution(res.data?.resolution);
    if (!mapped) {
      throw new Error('cockpitResolutionApi.resolveCheckFinding: malformed server response.');
    }
    return { resolution: mapped };
  } catch (err) {
    throw normalizeCallableError(err);
  }
}

/**
 * Loads the full resolution audit trail for one document (ascending by
 * `createdAt`, i.e. server order — oldest first). Malformed entries are
 * discarded (logged), never surfaced as fabricated data.
 *
 * @param mandantUid - the mandant's scope uid.
 * @param docId - the inbound document id.
 * @returns the audit trail (possibly empty).
 * @throws Error (normalised) on scope/authorization or unexpected failures.
 */
export async function listCheckResolutions(
  mandantUid: string,
  docId: string,
): Promise<ListCheckResolutionsResult> {
  const callable = httpsCallable<
    { mandantUid: string; docId: string },
    { resolutions: unknown[] }
  >(functions, 'listCheckResolutions');
  try {
    const res = await callable({ mandantUid, docId });
    const rawList = Array.isArray(res.data?.resolutions) ? res.data.resolutions : [];
    const resolutions: CheckResolution[] = [];
    for (const raw of rawList) {
      const mapped = mapRawResolution(raw);
      if (mapped) {
        resolutions.push(mapped);
      } else {
        logger.warn('cockpitResolutionApi.listCheckResolutions: discarding malformed entry', { docId });
      }
    }
    return { resolutions };
  } catch (err) {
    throw normalizeCallableError(err);
  }
}

/**
 * Maps a Firebase callable error to a plain Error, preserving the server
 * message and exposing the coarse error code (e.g. `permission-denied`)
 * without leaking internals.
 *
 * @param err - the raw callable rejection.
 * @returns a plain Error carrying an optional `code`.
 */
function normalizeCallableError(err: unknown): Error & { code?: string } {
  const anyErr = err as { code?: string; message?: string };
  const message =
    typeof anyErr?.message === 'string' && anyErr.message.length > 0
      ? anyErr.message
      : 'Request failed.';
  const out = new Error(message) as Error & { code?: string };
  if (typeof anyErr?.code === 'string') {
    out.code = anyErr.code.replace(/^functions\//, '');
  }
  return out;
}
